import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const LowStockAlerts = () => {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const fetchLowStockAlerts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(API_ENDPOINTS.BRANCH_LOW_STOCK)
      const data = Array.isArray(response.data) ? response.data : []
      setAlerts(data)
    } catch (err) {
      console.error('Error fetching low stock alerts:', err)
      const message = err.response?.data?.detail || err.response?.data?.message || 'Failed to load low stock alerts. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLowStockAlerts()
  }, [fetchLowStockAlerts])

  const formatNumber = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return '0.00'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const filteredAlerts = alerts.filter(alert => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      alert.product_name?.toLowerCase().includes(search) ||
      alert.product_sku?.toLowerCase().includes(search) ||
      alert.branch_name?.toLowerCase().includes(search)
    )
  })

  const getStockStatus = (quantity, reorderLevel) => {
    const qty = parseFloat(quantity) || 0
    const reorder = parseFloat(reorderLevel) || 0
    
    if (qty === 0) {
      return { badge: 'bg-danger', text: 'Out of Stock' }
    } else if (qty < reorder) {
      return { badge: 'bg-warning', text: 'Low Stock' }
    } else {
      return { badge: 'bg-success', text: 'In Stock' }
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Low Stock Alerts</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Inventory</li>
            <li className="breadcrumb-item active">Low Stock Alerts</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Low Stock Alerts</h5>
                
                {/* Search Filter */}
                <div className="mb-3">
                  <div className="input-group" style={{ maxWidth: '400px' }}>
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by product name, SKU, or branch..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError(null)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {/* Loading State */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading low stock alerts...</p>
                  </div>
                ) : filteredAlerts.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">
                      {searchTerm ? 'No alerts found matching your search.' : 'No low stock alerts at this time.'}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Product Name</th>
                          <th scope="col">SKU</th>
                          <th scope="col">Branch</th>
                          <th scope="col" className="text-end">Current Quantity</th>
                          <th scope="col" className="text-end">Reorder Level</th>
                          <th scope="col" className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAlerts.map((alert) => {
                          const status = getStockStatus(alert.quantity, alert.reorder_level)
                          return (
                            <tr key={`${alert.product_id}-${alert.branch_id}`}>
                              <td>
                                <strong>{alert.product_name || '-'}</strong>
                              </td>
                              <td>
                                <code>{alert.product_sku || '-'}</code>
                              </td>
                              <td>{alert.branch_name || '-'}</td>
                              <td className="text-end">
                                <strong>{formatNumber(alert.quantity)}</strong>
                              </td>
                              <td className="text-end">{formatNumber(alert.reorder_level)}</td>
                              <td className="text-center">
                                <span className={`badge ${status.badge}`}>
                                  {status.text}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Summary */}
                {!loading && filteredAlerts.length > 0 && (
                  <div className="mt-3">
                    <p className="text-muted small">
                      Showing <strong>{filteredAlerts.length}</strong> of <strong>{alerts.length}</strong> alert{alerts.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default LowStockAlerts

